/**
 * TERRA API Client — SSE streaming + REST endpoints
 */
import { apiClient } from '@/api/client';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ── Types ───────────────────────────────────────────────────────────────

export interface Conversation {
  id: string;
  site_id: string | null;
  title: string;
  message_count: number;
  total_tokens: number;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool_call' | 'tool_result';
  content: string | null;
  tool_name: string | null;
  tool_input: Record<string, unknown> | null;
  tool_output: Record<string, unknown> | null;
  ui_actions: TerraAction[] | null;
  tokens_used: number;
  latency_ms: number | null;
  rating: number | null;
  feedback_text: string | null;
  created_at: string;
}

export interface ConversationDetail extends Conversation {
  model_used: string;
  messages: Message[];
}

export async function submitFeedback(
  messageId: string,
  rating: 1 | -1,
  feedbackText?: string,
): Promise<void> {
  const { apiClient } = await import('./client');
  await apiClient.patch(`/terra/messages/${messageId}/feedback`, {
    rating,
    feedback_text: feedbackText,
  });
}

export interface ExtractedAttachment {
  filename: string;
  text: string;
  char_count: number;
  truncated: boolean;
}

/**
 * Extract plain text from a file for an ephemeral (non-persisted) attach.
 * The caller is responsible for splicing the returned text into the next
 * outgoing message — nothing is written to TERRA's embeddings.
 */
export async function extractAttachmentText(file: File): Promise<ExtractedAttachment> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ExtractedAttachment>(
    '/terra/extract-text',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export type TerraAction =
  | { type: 'navigate'; tab: string; chartType?: string; siteId?: string; wellNames?: string[]; analyteCodes?: string[]; dateFrom?: string; dateTo?: string }
  | { type: 'navigate_page'; path: string }
  | { type: 'filter'; filters: Record<string, unknown> }
  | { type: 'highlight'; wellNames: string[]; color?: string }
  | { type: 'scroll_to_chart'; chartId: string }
  | { type: 'clear_highlights' };

export interface Citation {
  index: number;
  tool_name: string;
  label: string;
}

// Inline renderable components from tool results
export type InlineComponent =
  | { type: 'inline_table'; title: string; headers: string[]; rows: any[][] }
  | { type: 'inline_chart'; chart_type: 'line' | 'bar' | 'area'; title: string; series: { name: string; data: { x: string; y: number | null }[] }[]; x_label?: string; y_label?: string }
  | { type: 'inline_metrics'; title: string; metrics: { label: string; value: string | number; unit?: string; status?: string }[] };

export interface StreamCallbacks {
  onStart?: (data: { conversation_id: string; message_id: string }) => void;
  onDelta?: (data: { text: string }) => void;
  onToolStart?: (data: { tool_name: string; tool_input: Record<string, unknown> }) => void;
  onToolEnd?: (data: { tool_name: string; summary: string; duration_ms: number }) => void;
  onAction?: (action: TerraAction) => void;
  onUIComponent?: (data: InlineComponent) => void;
  onThinking?: (data: { text: string }) => void;
  onDone?: (data: { tokens_used: number; latency_ms: number; suggestions?: string[]; citations?: Citation[] | null }) => void;
  onError?: (data: { code: string; message: string }) => void;
}

// ── REST Endpoints ──────────────────────────────────────────────────────

export async function createConversation(siteId?: string, title?: string): Promise<Conversation> {
  const { data } = await apiClient.post('/terra/conversations', {
    site_id: siteId,
    title,
  });
  return data;
}

export async function listConversations(siteId?: string, limit = 20): Promise<Conversation[]> {
  const params: Record<string, string | number> = { limit };
  if (siteId) params.site_id = siteId;
  const { data } = await apiClient.get('/terra/conversations', { params });
  return data;
}

export async function loadConversation(conversationId: string): Promise<ConversationDetail> {
  const { data } = await apiClient.get(`/terra/conversations/${conversationId}`);
  return data;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`/terra/conversations/${conversationId}`);
}

export async function searchConversations(query: string): Promise<Conversation[]> {
  const { data } = await apiClient.get('/terra/conversations/search', { params: { q: query } });
  return data;
}

// ── SSE Streaming ───────────────────────────────────────────────────────

/**
 * Stream a message to TERRA via SSE.
 * Returns an AbortController to cancel the stream.
 */
export function streamMessage(
  conversationId: string,
  content: string,
  context: Record<string, unknown> | null,
  callbacks: StreamCallbacks,
): AbortController {
  const controller = new AbortController();

  const url = `${API_BASE_URL}/api/v1/terra/conversations/${conversationId}/messages`;

  const doFetch = () => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    signal: controller.signal,
    body: JSON.stringify({ content, context }),
  });

  doFetch()
    .then(async (response) => {
      // If 401, try refreshing the auth cookie and retry once
      if (response.status === 401) {
        try {
          await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
          });
          response = await doFetch();
        } catch {
          // Refresh failed — fall through to error handler
        }
      }
      if (!response.ok) {
        callbacks.onError?.({
          code: 'http_error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        });
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let streamFinalized = false;
      let chunkCount = 0;

      const processLines = (lines: string[]) => {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (currentEvent) {
                case 'start':
                  callbacks.onStart?.(data);
                  break;
                case 'delta':
                  callbacks.onDelta?.(data);
                  break;
                case 'tool_start':
                  callbacks.onToolStart?.(data);
                  break;
                case 'tool_end':
                  callbacks.onToolEnd?.(data);
                  break;
                case 'action':
                  callbacks.onAction?.(data as TerraAction);
                  break;
                case 'ui_component':
                  callbacks.onUIComponent?.(data as InlineComponent);
                  break;
                case 'thinking':
                  callbacks.onThinking?.(data);
                  break;
                case 'done':
                  streamFinalized = true;
                  callbacks.onDone?.(data);
                  break;
                case 'error':
                  streamFinalized = true;
                  callbacks.onError?.(data);
                  break;
              }
            } catch {
              // malformed JSON — skip
            }
            currentEvent = '';
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunkCount++;
        const decoded = decoder.decode(value, { stream: true });
        buffer += decoded;

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        processLines(lines);
      }

      // Flush remaining buffer
      if (buffer.trim()) {
        processLines(buffer.split('\n'));
      }

      // Safety net: if stream ended without done/error event, finalize
      if (!streamFinalized) {
        callbacks.onDone?.({ tokens_used: 0, latency_ms: 0 });
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        callbacks.onError?.({
          code: 'network_error',
          message: err.message || 'Network request failed',
        });
      }
    });

  return controller;
}
